import React, { useState } from 'react';
import styles from './styles.module.scss';
import Typography from '@components/common/typography';
import Button from '@components/common/button';
import Input from '@components/common/input';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { apiAdminService } from '@api';
import { useForm } from '@hooks/helpers/useForm';
import FormField from '@components/form_field';
import { FormProvider } from 'react-hook-form';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { toast } from 'react-toastify';
import { useLocales } from '@hooks/helpers/useLocales';
import { convertStringsToNumbers } from '@utils/index';
import { EUserType, TLimitsSettings } from '@xeppt/xeppt-sdk/types';
import { BUSINESS_ONLY_OPERATIONS, INDIVIDUAL_ONLY_OPERATIONS, EFundsOperation } from '@enum';
import { ELoyaltyLevel } from '@xeppt/xeppt-sdk/types/loyalty';

// Transaction-type labels MUST match the consumer portal (xeppt-portal loyalty
// operationNames) and the admin Momentum tab — keep all three in sync.
//
// Category B (main-api-webhooks docs/superpowers/specs/2026-07-12-transaction-knobs-wiring-design.md):
// the LIMIT (min/max) rows for E_TRANSFER_DEBIT_TRANSFER (3_2_4), CARD_CREDIT_WITHDRAWAL
// (6_1_10), CARD_DEBIT_DEPOSIT (6_2_9), and WALLET_DEBIT_PAYOUT (1_2_16) are
// INFORMATIONAL ONLY. Each is reported by a provider (Pateno e-Transfer/card, or the
// payout job) AFTER the money already moved — inbound e-Transfer deposit, card auth
// clearing the network, payout already in flight — so the backend has no
// CheckUserLimits call to enforce a min/max on that path (see the corresponding
// comments in webhook_update_etransfer.go, webhook_card_transaction.go, and
// merchant_payout_service.go). The rows stay visible/editable so admins retain the
// setting for when pre-authorization controls exist (out of scope today); do not
// assume setting a value here rejects these transaction types.
export const paymentMethods = [
    { key: EFundsOperation.WALLET_CREDIT_TRANSFER, label: 'Send money via XEPPT' },
    { key: EFundsOperation.WALLET_DEBIT_TRANSFER, label: 'Receive money via XEPPT' },
    { key: EFundsOperation.WALLET_CREDIT_REQUEST, label: 'Approve someone’s money request' },
    { key: EFundsOperation.WALLET_DEBIT_REQUEST, label: 'Request money via XEPPT (when approved)' },
    { key: EFundsOperation.WALLET_CREDIT_CARD_DEPOSIT, label: 'Load XEPPT card balance' },
    // Card-service (1_1_8) is a fixed product price set under /admin/products, not a
    // per-transaction min/max, and the backend does not enforce a limit on it — left
    // out of /admin/limits so admins don't configure a value that has no effect.
    // { key: EFundsOperation.WALLET_CREDIT_CARD_SERVICE, label: 'Order XEPPT card' },
    { key: EFundsOperation.WALLET_DEBIT_CASHBACK, label: 'Redeem cashback' },
    { key: EFundsOperation.PAY_BILL_CREDIT_PAYMENT, label: 'Pay a bill' },
    {
        key: EFundsOperation.E_TRANSFER_CREDIT_TRANSFER,
        label: 'Send money via Interac e-Transfer®'
    },
    {
        key: EFundsOperation.E_TRANSFER_DEBIT_TRANSFER,
        label: 'Receive money via Interac e-Transfer®'
    },
    {
        key: EFundsOperation.E_TRANSFER_DEBIT_REQUEST,
        label: 'Request money via Interac e-Transfer® (when approved)'
    },
    { key: EFundsOperation.EFT_CREDIT_WITHDRAWAL, label: 'Withdraw to linked bank account' },
    { key: EFundsOperation.EFT_DEBIT_DEPOSIT, label: 'Load balance from linked bank account' },
    { key: EFundsOperation.EPS_DEBIT_DEPOSIT, label: 'Load balance from linked card' },
    { key: EFundsOperation.CARD_CREDIT_WITHDRAWAL, label: 'Pay with XEPPT card' },
    { key: EFundsOperation.CARD_DEBIT_DEPOSIT, label: 'XEPPT card load balance' },
    { key: EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD, label: 'Issue XEPPT gift card' },
    // Activate (8_1_15) reserves the card's face value from the merchant wallet when the
    // card is activated/sent. The backend enforces this limit fail-closed, so it must be
    // configurable here. It is a face-value hold (not a fee) → Limits only, no Fees row.
    { key: EFundsOperation.GIFT_CARD_CREDIT_RESERVE, label: 'Activate XEPPT gift card (reserve)' },
    { key: EFundsOperation.GIFT_CARD_DEBIT_GIFT_CARD_REDEEM, label: 'Redeem XEPPT gift card' },
    { key: EFundsOperation.WALLET_DEBIT_PAYMENT, label: 'Receive a Pay with XEPPT payment' },
    { key: EFundsOperation.WALLET_CREDIT_PAYMENT, label: 'Make a Pay with XEPPT payment' },
    // Payout legs follow this file's CREDIT = money OUT / DEBIT = money IN convention
    // (as with the TRANSFER/PAYMENT/EFT rows above, and backend
    // models/transaction_types.go): WALLET_CREDIT_PAYOUT (1_1_16) is the merchant's
    // outbound send leg — business-only, gated in BUSINESS_ONLY_OPERATIONS;
    // WALLET_DEBIT_PAYOUT (1_2_16) is the customer's inbound receive leg, which
    // individuals do use, so it stays on the Personal tab.
    { key: EFundsOperation.WALLET_CREDIT_PAYOUT, label: 'Send a payout (merchant)' },
    { key: EFundsOperation.WALLET_DEBIT_PAYOUT, label: 'Receive a payout' },
    // Merchant Connect/PWX order refund (backend-enforced business-only, key 1_2_1) —
    // see BUSINESS_ONLY_OPERATIONS in @enum for backend citations.
    {
        key: EFundsOperation.WALLET_DEBIT_REFUND,
        label: 'Refund a Pay with XEPPT payment (merchant)'
    }
];

// Individuals cannot reach any operation in BUSINESS_ONLY_OPERATIONS
// (backend-enforced 403), so those rows are hidden on the Personal tab; likewise
// businesses cannot reach any operation in INDIVIDUAL_ONLY_OPERATIONS (e.g.
// cashback redeem), so those are hidden on the Business tab. Every other row —
// including gift-card redeem and the Connect purchase/payout-receive legs, which
// individuals do use — is shown for both user types.
export const getPaymentMethodsForUserType = (userType: EUserType) =>
    userType === EUserType.INDIVIDUAL
        ? paymentMethods.filter((item) => !BUSINESS_ONLY_OPERATIONS.includes(item.key))
        : paymentMethods.filter((item) => !INDIVIDUAL_ONLY_OPERATIONS.includes(item.key));
// Helper function to get only numeric values from enum
const getEnumNumericValues = (enumObject: any): number[] => {
    return Object.values(enumObject).filter((value) => typeof value === 'number') as number[];
};

const LimitsLayout = () => {
    const [activeTab, setActiveTab] = useState<EUserType>(EUserType.INDIVIDUAL);
    const [activeLevel, setActiveLevel] = useState<ELoyaltyLevel>(ELoyaltyLevel.A);
    const { requestErrorLocale } = useLocales();
    const [isEdit, setIsEdit] = useState(false);
    const form = useForm({});

    const { data: limits, handleRequest: refetchLimits } = useApiQuery({
        method: () => apiAdminService.getLimits(),
        onSuccess: (data) => {
            form.reset(data);
        }
    });

    const { handleRequest: handleUpdateSettings, isLoading } = useApiMutation({
        method: () =>
            apiAdminService.updateLimits(
                convertStringsToNumbers(form.getValues()) as TLimitsSettings
            ),
        onSuccess: () => {
            setIsEdit(false);
            toast.success('Settings were successfully updated');
            refetchLimits();
        },
        onError: requestErrorLocale
    });

    const renderFormField = (fieldName: string) => (
        <FormField
            name={fieldName}
            renderComponent={(props) => (
                <Input
                    readOnly={!isEdit}
                    onlyNumbers
                    prefix="CAD"
                    full
                    placeholder="0.00"
                    fixedAmount
                    hideErrorIcon
                    {...props}
                />
            )}
        />
    );

    const renderTable = (userType: EUserType, level: ELoyaltyLevel) => (
        <table
            className={styles.table}
            style={{
                display: activeTab === userType && activeLevel === level ? 'table' : 'none'
            }}>
            <tbody>
                <tr>
                    <td></td>
                    <td>Per transaction</td>
                    <td>Daily</td>
                    <td>Weekly</td>
                    <td>Monthly</td>
                    <td>Min amount</td>
                </tr>
                {getPaymentMethodsForUserType(userType).map((item) => (
                    <tr key={item.key}>
                        <td>{item.label}</td>
                        <td>{renderFormField(`${userType}.${level}.${item.key}.per_transfer`)}</td>
                        <td>{renderFormField(`${userType}.${level}.${item.key}.daily`)}</td>
                        <td>{renderFormField(`${userType}.${level}.${item.key}.weekly`)}</td>
                        <td>{renderFormField(`${userType}.${level}.${item.key}.monthly`)}</td>
                        <td>
                            {renderFormField(
                                `${userType}.${level}.${item.key}.amount_transfer_min`
                            )}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );

    // Get only numeric values from enums
    const userTypes = getEnumNumericValues(EUserType) as EUserType[];
    const loyaltyLevels = getEnumNumericValues(ELoyaltyLevel) as ELoyaltyLevel[];

    return (
        <div className={styles.wrapper}>
            <FormProvider {...form}>
                <div className={styles.header}>
                    <div className={styles.text_wrapper}>
                        <Typography variant="h4">XEPPT Limits</Typography>
                    </div>
                </div>
                <div className={styles.actions_content}>
                    <div className={styles.actions_wrapper}>
                        <Button
                            size="normal"
                            onClick={() => setActiveTab(EUserType.INDIVIDUAL)}
                            variant={
                                activeTab === EUserType.INDIVIDUAL ? 'primary' : 'outlined-dark'
                            }>
                            Personal
                        </Button>
                        <Button
                            size="normal"
                            onClick={() => setActiveTab(EUserType.BUSINESS)}
                            variant={
                                activeTab === EUserType.BUSINESS ? 'primary' : 'outlined-dark'
                            }>
                            Business
                        </Button>
                    </div>
                    <div className={styles.actions}>
                        {!isEdit && (
                            <Button
                                size="normal"
                                variant="outlined"
                                leftIcon="edit"
                                onClick={() => setIsEdit(true)}>
                                Edit
                            </Button>
                        )}
                        {isEdit && (
                            <>
                                <Button
                                    size="normal"
                                    variant="outlined-dark"
                                    onClick={() => {
                                        setIsEdit(false);
                                        form.reset(limits);
                                    }}>
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleUpdateSettings}
                                    isLoading={isLoading}
                                    size="normal"
                                    variant="primary">
                                    Save Changes
                                </Button>
                            </>
                        )}
                    </div>
                </div>
                <div className={styles.content}>
                    <div className={styles.block}>
                        <div className={styles.divider} />
                        <div className={styles.actions_wrapper}>
                            <Button
                                size="normal"
                                onClick={() => setActiveLevel(ELoyaltyLevel.A)}
                                variant={
                                    activeLevel === ELoyaltyLevel.A ? 'primary' : 'outlined-dark'
                                }>
                                Silver
                            </Button>
                            <Button
                                size="normal"
                                onClick={() => setActiveLevel(ELoyaltyLevel.B)}
                                variant={
                                    activeLevel === ELoyaltyLevel.B ? 'primary' : 'outlined-dark'
                                }>
                                Gold
                            </Button>
                            <Button
                                size="normal"
                                onClick={() => setActiveLevel(ELoyaltyLevel.C)}
                                variant={
                                    activeLevel === ELoyaltyLevel.C ? 'primary' : 'outlined-dark'
                                }>
                                Platinum
                            </Button>
                        </div>

                        {/* Render all possible combinations but only show the active one */}
                        {userTypes.map((userType) =>
                            loyaltyLevels.map((level) => (
                                <div key={`${userType}-${level}`}>
                                    {renderTable(userType, level)}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </FormProvider>
        </div>
    );
};

export default LimitsLayout;
